import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer } from '../components/Toast';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 3500, action = null) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, action }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
    return id;
  }, []);

  const toast = {
    success: (msg) => addToast(msg, 'success', 3500),
    error:   (msg) => addToast(msg, 'error', 5000),
    info:    (msg) => addToast(msg, 'info', 3500),
    warn:    (msg) => addToast(msg, 'warn', 4000),

    /**
     * A toast with an action button (C11's Undo).
     *
     * The window is deliberately generous — 8 s rather than the usual 3.5 —
     * because an accidental archive is noticed a beat after it happens, and a
     * toast that has already vanished is the same as no undo at all.
     *
     * @param {string} msg
     * @param {{label: string, onAction: Function}} action
     */
    withAction: (msg, action, { type = 'success', duration = 8000 } = {}) =>
      addToast(msg, type, duration, {
        label: action.label,
        onAction: (id) => {
          // Dismiss first: leaving the button live while the action runs invites
          // a double-undo, which here would mean restoring twice.
          dismissToast(id);
          action.onAction();
        },
      }),

    dismiss: dismissToast,
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
}

/**
 * Returns { success, error, info, warn } toast functions.
 * Must be used inside <ToastProvider>.
 */
export function useToast() {
  return useContext(ToastContext);
}
